import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomToolTip from '../../../../ToolTipInfoCustomMUI';
import Zoom from '@mui/material/Zoom';
import apart_styles from '../../../../../styles/aprt.module.scss';
import gridStyles from '../styles/compartmentsGrid.module.scss';

/**
 * Compartment Tile - Grid-based UI element for displaying compartment info
 * Inspired by InfoBlockNEW.jsx apartment_info_data_element structure
 * 
 * Layout:
 * ┌─────────────┬─────────────┬─────────────┐
 * │ Label 1     │ Label 2     │ Label 3     │
 * │ Data 1      │ Data 2      │ Data 3      │
 * ├─────────────┴─────────────┴─────────────┤
 * │ Expanded details for selected tile      │
 * └─────────────────────────────────────────┘
 * 
 * @param {Object} compartment - Compartment data
 * @param {boolean} isSelected - Is this tile selected/expanded
 * @param {boolean} isHovered - Is this tile hovered
 * @param {Function} onClick - Click handler
 * @param {Function} onHover - Mouse enter handler
 * @param {Function} onHoverLeave - Mouse leave handler
 * @param {string} labelText - Label to display (compartment name)
 * @param {string|JSX} valueContent - Value/data to display (area, etc.)
 * @param {JSX} [expandedContent] - Content shown when tile is selected
 * @param {boolean} [hasTooltip] - Should show info tooltip
 * @param {string|JSX} [tooltipContent] - Tooltip content
 * @param {Object} [style] - Additional inline styles
 */
function CompartmentTile({
	compartment,
	isSelected,
	isHovered,
	onClick,
	onHover,
	onHoverLeave,
	labelText,
	valueContent,
	expandedContent = null,
	hasTooltip = false,
	tooltipContent = null,
	style = {},
}) {
	const { t } = useTranslation();

	const tileClassName = `
		${gridStyles.apartment_info_data_element}
		${isSelected ? gridStyles.compartment_tile_selected : ''}
		${isHovered ? gridStyles.compartment_tile_hovered : ''}
	`.trim();

	return (
		<>
			<div
				className={tileClassName}
				onClick={onClick}
				onMouseEnter={onHover}
				onMouseLeave={onHoverLeave}
				style={{
					cursor: 'pointer',
					transition: 'all 0.2s ease',
					...style, // Merge additional styles
				}}
			>
				<span className={apart_styles.apartment_info_data_type}>
					{labelText}
					{hasTooltip && tooltipContent && (
						<CustomToolTip
							title={tooltipContent}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button className={gridStyles.infoToolTip__button}>?</button>
						</CustomToolTip>
					)}
				</span>
				<span className={apart_styles.apartment_info_data_value}>
					{valueContent}
				</span>
			</div>
			
			{/* Expanded details section - shown when tile is selected */}
			{isSelected && expandedContent && (
				<div className={gridStyles.compartment_tile_expanded}>
					{expandedContent}
				</div>
			)}
		</>
	);
}

export default CompartmentTile;
